import {
  MarketoFormInterface,
  TrackerEvent,
  MenuLink as themeMenuLink
} from '@lucid/gatsby-theme-lucid';
import {
  DemoRequestClientsJsonConnection,
  Drupal,
  Drupal_ParagraphMarketoForm,
  EnterpriseHeaderLogosJsonConnection,
  FileConnection,
  Maybe,
  ProductTemplatesJsonConnection,
  Scalars
} from '../generated/graphql';

export interface PageContext {
  nid: string;
  langPrefix: string;
  LanguageId: string;
  i18nKey: string;
  pageType: string;
  extraData: string;
  entityChanged?: string;
  page?: number;
  pageCount?: number;
  pageSize?: number;
  hubAlias?: string;
  excludeFromSitemap?: boolean;
  documentationType?: string;
  categories?: string[];
  persona?: string;
  hideSsr: boolean;
}

export interface MenuLink extends themeMenuLink {
  type?: string;
  truncate?: boolean;
  fieldLinkPhoneNumber?: string;
  fieldKey?: string;
  fieldBeaconElementDetail?: string;
  fieldKmEventName?: string;
  fieldMarketo?: {
    entity: Drupal_ParagraphMarketoForm;
  };
  fieldIsbigdropdown?: boolean;
  fieldAddonBlurb?: string;
  fieldThumbnail?: string;
  links: MenuLink[];
  entityId?: string;
  entityUuid?: string;
  entityBundle?: string;
}

export interface Data {
  drupal: Drupal;
  i18nData?: FileConnection;
  allDemoRequestClientsJson?: DemoRequestClientsJsonConnection;
  allEnterpriseHeaderLogosJson?: EnterpriseHeaderLogosJsonConnection;
  allProductTemplatesJson?: ProductTemplatesJsonConnection;
}

export interface TemplateProps {
  data: Data;
  pageContext: PageContext;
  location: {
    pathname: string;
    origin: string;
    search: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    state: any;
  };
}

export interface ExtraData {
  marketoForms: { [key: string]: MarketoFormInterface };
}

export interface MarketoFormField {
  targetId?: Maybe<Scalars['Int']>;
  entity?: Maybe<Drupal_ParagraphMarketoForm>;
  targetRevisionId?: Maybe<Scalars['Int']>;
}

export function isDefined<T extends object>(
  input: null | undefined | T | string
): input is T {
  return input != null;
}

export enum Template {
  CaseStudy = 'case_study',
  CaseStudyHub = 'case_study_hub',
  Consideration = 'consideration',
  ConsiderationCompetitor = 'consideration-competitor',
  ConsiderationCompetitorPpc = 'consideration-competitor-ppc',
  ConsiderationPpc = 'consideration_ppc',
  DemoRequest = 'demo_request',
  Discovery = 'discovery',
  Documentation = 'documentation',
  Ebook = 'ebook',
  Enterprise = 'enterprise',
  EnterpriseRegistration = 'enterprise_registration',
  FifteenIntegration = 'fifteen_integration',
  FifteenIntegrationPpc = 'fifteen_integration_ppc',
  FifteenOg = 'fifteen_og',
  FifteenOgCompetitor = 'fifteen_og_competitor',
  FifteenOgPpc = 'fifteen_og_ppc',
  FormPage = 'form_page',
  FunFlowchart = 'fun_flowchart',
  FunFlowchartHub = 'fun_flowchart_hub',
  Homepage = 'homepage',
  IndividualRole = 'individual_role',
  IntegrationHub = 'integration_hub',
  Nineteen = 'nineteen',
  NineteenSolutions = 'nineteen_solutions',
  Partners = 'partners',
  ProductTemplate = 'product_template',
  ProductTemplateHub = 'product_template_hub',
  RolesHub = 'roles_hub',
  SolutionsHub = 'solutions_hub',
  Sunrise = 'sunrise',
  TourPage = 'tour_page',
  Webinar = 'webinar',
  WebinarHub = 'webinar_hub',
  WhitePaper = 'white_paper'
}

export enum WeekDay {
  Sunday = 0,
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday
}

export interface Tracker {
  [key: string]:
    | Array<{
        entity?: {
          entityLabel?: string | null;
        } | null;
      } | null>
    | {
        entity?: {
          entityLabel?: string | null;
        } | null;
      }
    | undefined
    | null;
}

interface DriftPayload {
  isOnline: boolean; // is the widget "online"
  sidebarOpen: boolean; // was the sidebar opened at widget launch
  teamAvailability: object;
  widgetVisible: boolean; // is the widget visible based on the drift display settings to the user (excludes any manual css overrides)
}

declare global {
  interface Window {
    _wq: object[];
    ga: (
      str: string,
      obj: object | string,
      third?: object | string | null
    ) => void;
    drift: {
      on: (
        str: string,
        funct: (
          api: {
            startInteraction: (obj: object) => void;
          },
          payload: DriftPayload
        ) => void
      ) => void;
      page: () => void;
    };
    Wistia: {
      api: {
        all: () => Array<{
          email: (str: string) => void;
        }>;
      };
      localStorage: (key: string, value?: string) => string;
    };
    lucid: {
      getMetricLogger?: (
        str: string
      ) => {
        markLoaded: () => void;
        tag: (str: string, val?: string | boolean) => void;
      };
      kissMetrics?: {
        set: (val: { [x: string]: string | number }) => void;
        recordEvent: (val: string) => void;
      };
      beacon: {
        recordEvent: (evt: TrackerEvent) => void;
      };
    } | null;
    dataLayer: object[] | null;
    optimizely: {
      push: any;
      get: any;
    };
    optimizelyData: any;
    optimizelyActionApplied: boolean | null;
  }
}
